import { Box, Typography } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"

import type { ProductInventoriesSearchParams } from "../../schemas"

import { FilterChip } from "@/components/common/Filters"
import type { Option } from "@/types/form-fields"

interface ProductInventoryFilterChipsProps {
  searchParams: ProductInventoriesSearchParams
  productStatusOptions: Option<string>[]
  productInventoryStatusOptions: Option<string>[]
  productManagerOptions: Option<string>[]
  productClassOptions: Option<string>[]
  onRemove: (key: keyof ProductInventoriesSearchParams, value?: string | number) => void
}

export const ProductInventoryFilterChips: FC<ProductInventoryFilterChipsProps> = ({
  searchParams,
  productStatusOptions,
  productInventoryStatusOptions,
  productManagerOptions,
  productClassOptions,
  onRemove,
}) => {
  const productStatuses = (searchParams.productStatuses ?? []).map(String)
  const inventoryStatuses = searchParams.inventoryStatuses ?? []
  const productManagerIds = searchParams.productManagerIds ?? []
  const productClassIds = searchParams.productClassIds ?? []

  const productStatusLabelsMap = useMemo(
    () => new Map(productStatusOptions.map((option) => [option.value, option.label])),
    [productStatusOptions]
  )
  const inventoryStatusLabelsMap = useMemo(
    () => new Map(productInventoryStatusOptions.map((option) => [option.value, option.label])),
    [productInventoryStatusOptions]
  )
  const productManagerLabelsMap = useMemo(
    () => new Map(productManagerOptions.map((option) => [option.value, option.label])),
    [productManagerOptions]
  )
  const productClassLabelsMap = useMemo(
    () => new Map(productClassOptions.map((option) => [option.value, option.label])),
    [productClassOptions]
  )

  const hasActiveFilters =
    productStatuses.length > 0 ||
    inventoryStatuses.length > 0 ||
    productManagerIds.length > 0 ||
    productClassIds.length > 0

  if (!hasActiveFilters) return null

  return (
    <Box className='flex flex-wrap items-center gap-2' data-testid='product-inventory.filter-chips'>
      <Typography variant='body1' className='text-gray-700'>
        Applied filters:
      </Typography>

      {productStatuses.map((value) => {
        const label = productStatusLabelsMap.get(value)

        if (!label) {
          return null
        }

        return (
          <FilterChip
            key={value}
            label={`Product Status: ${label}`}
            onDelete={() => onRemove("productStatuses", value)}
            testId={`product-inventory.filter-chips.product-status-${value}`}
          />
        )
      })}

      {inventoryStatuses.map((value) => {
        const label = inventoryStatusLabelsMap.get(value)

        if (!label) {
          return null
        }

        return (
          <FilterChip
            key={value}
            label={`Inventory Status: ${label}`}
            onDelete={() => onRemove("inventoryStatuses", value)}
            testId={`product-inventory.filter-chips.inventory-status-${value}`}
          />
        )
      })}

      {productManagerIds.map((id) => {
        const label = productManagerLabelsMap.get(id)

        if (!label) {
          return null
        }

        return (
          <FilterChip
            key={id}
            label={`Product Manager: ${label}`}
            onDelete={() => onRemove("productManagerIds", id)}
            testId={`product-inventory.filter-chips.product-manager-${id}`}
          />
        )
      })}

      {productClassIds.map((id) => {
        const label = productClassLabelsMap.get(id)

        if (!label) {
          return null
        }

        return (
          <FilterChip
            key={id}
            label={`Category/Class: ${label}`}
            onDelete={() => onRemove("productClassIds", id)}
            testId={`product-inventory.filter-chips.product-class-${id}`}
          />
        )
      })}
    </Box>
  )
}
